import type { ReactNode } from 'react';
import { Anchor, Button, Group, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useCompositionScope } from '@/hooks/api/automations/useExecutionCompositions.ts';
import { get_run_url } from '@/utils/urls.ts';
import type { Run } from '@/types/runs.ts';

/** A component/evidence Run can belong to a different Automation from the page. */
export default function CompositionRunLink({
  runId,
  children,
}: {
  runId: string;
  children: ReactNode;
}) {
  const api = useApiClient();
  const scope = useCompositionScope();
  const run = useQuery({
    queryKey: [...scope, 'run-owner', runId],
    enabled: Boolean(runId && scope[3]),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async ({ signal }) => {
      const { data } = await api.get<Run>(get_run_url(runId), { signal });
      if (data.id !== runId || typeof data.automation_id !== 'string' || !data.automation_id.trim())
        throw new Error('Run inspection does not confirm its owner.');
      return data;
    },
  });
  if (run.isError)
    return (
      <Group gap="xs">
        <Text size="sm">{children} · Run unavailable.</Text>
        <Button
          size="compact-xs"
          variant="subtle"
          disabled={run.isFetching}
          onClick={() => void run.refetch()}
        >
          Retry Run {runId}
        </Button>
      </Group>
    );
  if (!run.isFetchedAfterMount || !run.data)
    return <Text size="sm">{children} · Loading Run destination…</Text>;
  return (
    <Anchor component={Link} to={`/automations/${run.data.automation_id}/runs/${run.data.id}`}>
      {children}
    </Anchor>
  );
}
